import { generateKeyBetween } from 'fractional-indexing';

import { compareSortKeys } from './sort-key-compare';

export { compareSortKeys } from './sort-key-compare';

/**
 * Generate a sortKey for a card inserted between two neighbors.
 * Pass `null` for before/after to indicate start/end of list.
 */
export function sortKeyBetween(before: string | null, after: string | null): string {
  return generateKeyBetween(before, after);
}

/**
 * Generate N sortKeys between two neighbors, evenly spaced.
 * Useful when inserting multiple cards at once.
 */
export function sortKeysBetween(
  before: string | null,
  after: string | null,
  count: number,
): string[] {
  const keys: string[] = [];
  let prev = before;
  for (let i = 0; i < count; i++) {
    const key = generateKeyBetween(prev, after);
    keys.push(key);
    prev = key;
  }
  return keys;
}

/**
 * Assign sortKeys to cards that are missing them, preserving array order.
 *
 * CRITICAL — only the cards that are actually missing a key are touched.
 * Already-keyed cards keep BOTH their exact sortKey AND their object
 * reference. Missing cards are appended after the highest existing key
 * (or generated sequentially from scratch when the whole deck is
 * unkeyed). This reference-preservation is what stops the legacy-card
 * re-push flood: a single sortKey-less legacy card in a 500-card deck
 * used to reassign ALL 500 cards (new refs), and `detectDirty`'s
 * ref-changed scan + heal-on-touch then re-stamped and re-pushed the
 * whole deck on every load (out 11 MB, in 0 B for migrated accounts).
 *
 * Duplicate sortKeys (LWW merge / cross-device race — deterministic
 * `generateKeyBetween` means two offline devices appending to the same
 * deck mint identical keys for different cards) are repaired MINIMALLY:
 * only the second-and-later holders of a key get a fresh key, slotted
 * right after the first holder. The repair order is (sortKey, id) — a
 * function of card CONTENT, never of the input array — so every device
 * computes the identical repair and the fleet converges. The old
 * behavior here rebuilt EVERY key sequentially in input-array order;
 * the array at those call sites is store-map / storage-row order
 * (cardId-lexicographic ≈ random), so one duplicate permanently
 * scrambled the whole deck and synced the scramble group-wide.
 *
 * Post-condition either way: every card has a unique sortKey, array
 * order is preserved, and untouched cards keep their references.
 */
export function backfillSortKeys<T extends { id?: string; sortKey?: string }>(
  cards: T[],
): T[] {
  if (cards.length === 0) return cards;

  // Single pass: detect missing keys + duplicates.
  const seen = new Set<string>();
  let hasMissing = false;
  let hasDuplicate = false;
  for (const c of cards) {
    if (!c.sortKey) {
      hasMissing = true;
      continue;
    }
    if (seen.has(c.sortKey)) hasDuplicate = true;
    seen.add(c.sortKey);
  }

  // Fast path: every card already has a unique key — nothing to do.
  if (!hasMissing && !hasDuplicate) return cards;

  // New keys per card, keyed by object identity so the final map back to
  // input-array order can preserve every untouched reference.
  const rekeyed = new Map<T, string>();

  // Repair duplicates among the keyed cards. Walk in (sortKey, id) order —
  // deterministic across devices regardless of array order — keeping the
  // first holder of each key and re-keying later holders strictly between
  // the previous key and the next distinct existing key. Assigned keys are
  // strictly between existing ones, so they collide with nothing.
  const keyed = cards.filter((c) => c.sortKey);
  keyed.sort(
    (a, b) =>
      compareSortKeys(a.sortKey, b.sortKey) || compareSortKeys(a.id ?? '', b.id ?? ''),
  );
  let prev: string | null = null;
  for (let i = 0; i < keyed.length; i++) {
    const key = keyed[i].sortKey!;
    if (prev === null || key > prev) {
      prev = key;
      continue;
    }
    // Duplicate of `prev` — slot after it, before the next distinct key.
    let after: string | null = null;
    for (let j = i + 1; j < keyed.length; j++) {
      if (keyed[j].sortKey! > prev) {
        after = keyed[j].sortKey!;
        break;
      }
    }
    const fresh = generateKeyBetween(prev, after);
    rekeyed.set(keyed[i], fresh);
    prev = fresh;
  }

  // Missing keys: append after the highest key (post-repair `prev` is the
  // max; null when the whole deck is unkeyed → sequential a0,a1…) in
  // ARRAY order. Array order is the caller's contract: the legacy
  // Supabase→DO migration passes rows in the user's true legacy deck
  // order and relies on it surviving (legacy-migration.ts). Concurrent
  // client-side backfills of the same deck can therefore still mint
  // divergent keys — that's acceptable now that the resulting duplicates
  // get the minimal deterministic repair above instead of a full scramble.
  if (hasMissing) {
    for (const card of cards) {
      if (card.sortKey) continue;
      const key = generateKeyBetween(prev, null);
      rekeyed.set(card, key);
      prev = key;
    }
  }

  return cards.map((card) => {
    const fresh = rekeyed.get(card);
    return fresh === undefined ? card : { ...card, sortKey: fresh };
  });
}

/**
 * Reassign sortKeys to match a new ordering given by an array of items.
 * Returns fresh sequential sortKeys for the entire array.
 */
export function reassignSortKeys<T extends { sortKey?: string }>(cards: T[]): T[] {
  let prev: string | null = null;
  return cards.map((card) => {
    const key = generateKeyBetween(prev, null);
    prev = key;
    return { ...card, sortKey: key };
  });
}

/**
 * Does this `position` name a real slot to insert at, or does it mean
 * "append"?
 *
 * Only `undefined` ever meant append in the contract, but a caller resolving
 * an anchor hands over `NaN` or `-1` whenever the card it anchored to has
 * been deleted or has not loaded. Treating those as index 0 would mint keys
 * at the HEAD of the deck — reordering a deck nobody asked to reorder — so
 * anything that is not a real index appends instead.
 */
export function isInsertPosition(
  position: number | null | undefined,
): position is number {
  return position != null && Number.isInteger(position) && position >= 0;
}
